import { API_BASE_URL } from '~/config';
import { ApiCodeSamples, ApiCodeSamplesConfig } from './';

export function createInferenceApiCodeSamples(config: ApiCodeSamplesConfig, apiKey = '<API_KEY>'): ApiCodeSamples {
  const url = `${API_BASE_URL}${config.path ?? '/'}`;
  const headers = Object.entries(config.headers || {}).filter(([_key, value]) => value !== undefined);

  const jsonParams = JSON.stringify(config.body || {}, null, 2)
    // remove first opening bracket, and last closing bracket
    .slice(2, -2);
  const tsParams = JSON.stringify(config.body || {}, null, 4)
    .slice(2, -2)
    .replace(/"(?<temp1>[^"]+)":/gu, '$1:');
  const javaParams = JSON.stringify(config.body || {}, null, 4).slice(2, -2);
  const pythonParams = Object.entries(config.body || {})
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return `"${key}": "${value}"`;
      }
      if (typeof value === 'number') {
        return `"${key}": ${value}`;
      }
      if (typeof value === 'boolean') {
        return `"${key}": ${value ? 'True' : 'False'}`;
      }
      if (Array.isArray(value) || typeof value === 'object') {
        return `"${key}": ${JSON.stringify(value, null, 2).replaceAll('\n', '\n  ')}`;
      }
      return null;
    })
    .filter((x) => x !== null)
    .join(',\n  ');

  const javaExample = `URI uri = URI.create("${url}");
HttpClient client = HttpClient.newHttpClient();
HttpRequest request = HttpRequest.newBuilder()
  .uri(uri)
  ${headers
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `.header("${key}", "${value as string}")`)
    .join('\n  ')}
  .header("Authorization", "Bearer ${apiKey}")
  .${config.method || 'POST'}(HttpRequest.BodyPublishers.ofString("""{
${javaParams}
  }"""))
  .build();
HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());
  `;

  const goExample = `package main
  
import (
  "bytes"
  "net/http"
  "fmt"
)
  
apiUrl := "${url}"
var jsonData = []byte(\`{
${jsonParams}
}\`)

req, err := http.NewRequest(${config.method || 'POST'}, apiUrl, bytes.NewBuffer(jsonData))
${headers.map(([key, value]) => `req.Header.Set("${key}", "${value as string}")`).join('\n')}
req.Header.Set("Authorization", "Bearer ${apiKey}")

client := &http.Client{}
resp, err := client.Do(req)
if err != nil {
  panic(err)
}
defer resp.Body.Close()

fmt.Println("response Status:", resp.Status)`;

  const shellExample = `curl --request ${config.method || 'POST'} \\
  --url ${url} \\${headers.map(([key, value]) => `\n  -H '${key}: ${value as string}' \\`).join('')}
  -H 'Authorization: Bearer ${apiKey}' \\
  --data '{ 
${javaParams}
  }'`;

  const tsExample = `await fetch("${url}", {
  method: "${config.method || 'POST'}",
  headers: {${headers.map(([key, value]) => `\n    "${key}": "${value as string}",`).join('')}
    "Authorization": "Bearer ${apiKey}"
  },
  body: JSON.stringify({
${tsParams}
  })
});`;

  const pythonExample = `import requests
import json

url = "${url}"
payload = {
  ${pythonParams}
}
headers = {\n  ${headers.map(([key, value]) => `"${key}": "${value as string}",`).join('\n  ')}
  "Authorization": "Bearer ${apiKey}"
}
requests.request("${config.method || 'POST'}", url, headers=headers, data=json.dumps(payload))`;

  return {
    typescript: tsExample,
    python: pythonExample,
    java: javaExample,
    go: goExample,
    shell: shellExample,
  };
}
